import React from 'react';
import { 
  Users, 
  CreditCard, 
  CalendarCheck, 
  TrendingUp,
  Hotel,
  Bus,
  RefreshCw,
  Loader2,
  PoundSterling,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import SummaryPrintExport from './SummaryPrintExport';

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'violet', loading }) => {
  const colorClasses = {
    violet: 'bg-violet-50 text-violet-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    orange: 'bg-orange-50 text-orange-600',
    blue: 'bg-blue-50 text-blue-600',
    slate: 'bg-slate-100 text-slate-600'
  };

  return (
    <Card className="bg-white border-slate-200">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            {loading ? (
              <div className="h-8 w-20 bg-slate-100 rounded animate-pulse mt-1" />
            ) : (
              <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            )}
            {subtitle && (
              <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={cn("p-2.5 rounded-lg", colorClasses[color])}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RoomCard = ({ name, booked, capacity, pricePerPerson }) => {
  const occupancyPercent = capacity > 0 ? Math.round((booked / capacity) * 100) : 0;
  const remaining = Math.max(0, capacity - booked);
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-slate-900">{name}</h4>
        <Badge variant="outline" className="text-xs">
          £{pricePerPerson}/pp
        </Badge>
      </div>
      
      <div className="space-y-2">
        <Progress value={occupancyPercent} className="h-2" />
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">
            {booked} booked
          </span>
          <span className={cn(
            "font-medium",
            remaining === 0 ? "text-slate-500" : remaining <= 3 ? "text-orange-600" : "text-emerald-600"
          )}>
            {remaining === 0 ? "· Full" : `· ${remaining} available`}
          </span>
        </div>
      </div>
    </div>
  );
};

const AddOnCard = ({ name, booked, revenue }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
        <Bus className="w-4 h-4 text-violet-600" />
      </div>
      <div>
        <p className="font-medium text-slate-900">{name}</p>
        <p className="text-sm text-slate-500">{booked} booked</p>
      </div>
    </div>
    <div className="text-right">
      <p className="font-semibold text-slate-900">£{revenue.toFixed(2)}</p>
      <p className="text-xs text-slate-500">revenue</p>
    </div>
  </div>
);

export default function WeekenderOverview({ event, summaryData, loading, onRefresh }) {
  const financials = summaryData?.financials || {};
  const rooms = summaryData?.rooms || {};
  const addOns = summaryData?.add_ons || {};
  const statusBreakdown = summaryData?.bookings_by_status || {};

  // Calculate total capacity and occupancy
  const totalCapacity = Object.values(rooms).reduce((sum, r) => sum + (r.capacity || 0), 0);
  const totalBooked = Object.values(rooms).reduce((sum, r) => sum + (r.booked || 0), 0);
  const occupancyPercent = totalCapacity > 0 ? Math.round((totalBooked / totalCapacity) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Header with Refresh and Print */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Overview</h2>
          <p className="text-sm text-slate-500">Event summary and key metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <SummaryPrintExport 
            summaryData={summaryData} 
            disabled={loading} 
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            className="text-slate-500 hover:text-slate-700"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1.5" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Bookings"
          value={summaryData?.total_bookings || 0}
          subtitle={summaryData?.cancelled_bookings ? `${summaryData.cancelled_bookings} cancelled` : null}
          icon={CalendarCheck}
          color="violet"
          loading={loading}
        />
        <StatCard
          title="Total Guests"
          value={summaryData?.total_guests || 0}
          subtitle={`${occupancyPercent}% capacity`}
          icon={Users}
          color="blue"
          loading={loading}
        />
        <StatCard
          title="Amount Received"
          value={`£${(financials.total_amount_paid || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`}
          subtitle={`£${(financials.total_deposits_paid || 0).toFixed(2)} deposits`}
          icon={PoundSterling}
          color="emerald"
          loading={loading}
        />
        <StatCard
          title="Outstanding Balance"
          value={`£${(financials.total_balance_due || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`}
          subtitle={`${financials.pending_payment_count || 0} pending`}
          icon={Clock}
          color="orange"
          loading={loading}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Room Occupancy */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Hotel className="w-4 h-4 text-violet-600" />
                Room Occupancy
              </CardTitle>
              <Badge variant="outline">
                {totalBooked}/{totalCapacity} rooms
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : Object.keys(rooms).length > 0 ? (
              Object.entries(rooms).map(([type, data]) => (
                <RoomCard
                  key={type}
                  name={data.name || type}
                  booked={data.booked || 0}
                  capacity={data.capacity || 0}
                  pricePerPerson={data.price_per_person || 0}
                />
              ))
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">
                No room types configured
              </p>
            )}
          </CardContent>
        </Card>

        {/* Booking Status Breakdown + Add-ons */}
        <div className="space-y-5">
          {/* Booking Status */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-violet-600" />
                Booking Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-24 bg-slate-100 rounded animate-pulse" />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-slate-900">
                      {(statusBreakdown.fully_paid || 0) + (statusBreakdown.confirmed || 0)}
                    </p>
                    <p className="text-sm text-slate-500">Fully Paid</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-emerald-600">
                      {statusBreakdown.deposit_paid || 0}
                    </p>
                    <p className="text-sm text-slate-500">Deposit Paid</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-orange-600">
                      {statusBreakdown.pending || 0}
                    </p>
                    <p className="text-sm text-slate-500">Pending Payment</p>
                  </div>
                  <div className="bg-violet-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-violet-600">
                      {summaryData?.payment_methods?.payment_plan || 0}
                    </p>
                    <p className="text-sm text-slate-500">On Payment Plan</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add-ons */}
          {Object.keys(addOns).length > 0 && (
            <Card className="bg-white border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bus className="w-4 h-4 text-violet-600" />
                  Add-ons
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2].map(i => (
                      <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div>
                    {Object.entries(addOns).map(([name, data]) => (
                      <AddOnCard
                        key={name}
                        name={data.name || name}
                        booked={data.booked_count || 0}
                        revenue={data.revenue || 0}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Financial Summary */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-violet-600" />
            Financial Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-16 bg-slate-100 rounded animate-pulse" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-lg font-semibold text-slate-900">
                  £{(financials.total_revenue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Deposits Due</p>
                <p className="text-lg font-semibold text-slate-900">
                  £{(financials.total_deposits_due || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Deposits Paid</p>
                <p className="text-lg font-semibold text-emerald-600">
                  £{(financials.total_deposits_paid || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Received</p>
                <p className="text-lg font-semibold text-emerald-600">
                  £{(financials.total_amount_paid || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Balance Outstanding</p>
                <p className={cn(
                  "text-lg font-semibold",
                  (financials.total_balance_due || 0) > 0 ? "text-orange-600" : "text-emerald-600"
                )}>
                  £{(financials.total_balance_due || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
